import http from '../../shared/http.js';
import { initLiveList } from '../../shared/liveList.js';
import { confirmAction, notifyError, notifyInfo, notifySuccess } from '../../shared/notify.js';

// The log of the job-ad fetch runs (N10), plus the one action it offers:
// fetching today's ads now instead of waiting for the nightly tick.
//
// The list half is nothing but the live list - a run that already happened has
// no decision left to make.
//
// The button starts a run and gets its answer at once: reading the ads with
// Claude takes minutes, longer than a request may stay open. So the page polls
// its own table until the run is no longer `laeuft`, and reports what the run
// row says - the same numbers the log keeps, not a second count.

// How often the table is refetched while a run is going. The run takes
// minutes; a request every few seconds is enough to look alive without being
// noise in the server log.
const ABFRAGE_MS = 6000;

// When the page stops waiting. Far above any plausible run - past it the run
// is still recorded, the page just stops refreshing on its own.
const HOECHSTENS_MS = 30 * 60 * 1000;

function warte(ms) {
  return new Promise((fertig) => {
    window.setTimeout(fertig, ms);
  });
}

// The rows of runs still going, as the server rendered them.
function laufendeZeilen() {
  return document.querySelectorAll('#lauf-list tr[data-lauf-status="laeuft"]');
}

// What a finished run did, in the words the toast shows, read off its row.
function ergebnisText(zeile) {
  if (!zeile) return 'Abruf abgeschlossen. Das Ergebnis steht in der Tabelle.';

  const { laufStatus, geholt, jobs, verworfen, kunden } = zeile.dataset;
  if (laufStatus === 'fehler')
    return 'Der Abruf ist fehlgeschlagen. Der Grund steht in der Tabelle.';
  if (laufStatus === 'nicht-konfiguriert') {
    return 'Die Quelle ist nicht konfiguriert - es wurde nichts abgerufen.';
  }

  const teile = [`${geholt} Inserate abgerufen`];
  if (Number(jobs)) teile.push(`${jobs} Jobs erstellt`);
  if (Number(kunden)) teile.push(`${kunden} Kunden angelegt`);
  if (Number(verworfen)) teile.push(`${verworfen} nicht medizinisch verworfen`);
  return `${teile.join(', ')}.`;
}

function fehlermeldung(err) {
  return err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
}

export function initInseratLaeufe() {
  const live = initLiveList({
    results: 'lauf-list',
    path: '/inserate/laeufe',
    forms: ['lauf-filter']
  });
  if (!live) return;

  const starten = document.getElementById('abruf-starten');
  // Not on the page for a manager: the button is admin-only, like the endpoint
  // behind it.
  if (!starten) return;

  // Refetches the table until no run in it is going any more, then reports
  // the one that was. Keeps the button disabled meanwhile: a second press
  // would only earn a 409 from the overlap guard.
  async function begleiteLauf() {
    starten.disabled = true;
    const beginn = Date.now();

    try {
      await live.refresh();
      const laufend = laufendeZeilen()[0];
      const laufId = laufend ? laufend.dataset.laufId : null;

      while (laufendeZeilen().length && Date.now() - beginn < HOECHSTENS_MS) {
        await warte(ABFRAGE_MS);
        await live.refresh();
      }

      if (laufendeZeilen().length) {
        notifyInfo('Der Abruf läuft noch. Die Seite aktualisiert sich nicht mehr selbst.');
        return;
      }

      const zeile = laufId
        ? document.querySelector(`#lauf-list tr[data-lauf-id="${laufId}"]`)
        : null;
      if (zeile && zeile.dataset.laufStatus === 'fehler') notifyError(ergebnisText(zeile));
      else notifySuccess(ergebnisText(zeile));
    } finally {
      starten.disabled = false;
    }
  }

  starten.addEventListener('click', async () => {
    const bestaetigt = await confirmAction({
      title: 'Inserate abrufen',
      // Says what it costs and how long, because the source is metered and
      // the run now takes minutes rather than seconds.
      message:
        'Die heute bei der Quelle erfassten Inserate werden abgerufen, den Kunden zugeordnet und mit der KI aufbereitet. Das dauert einige Minuten; Sie können die Seite dabei offen lassen oder verlassen.',
      okText: 'Abrufen'
    });
    if (!bestaetigt) return;

    try {
      await http.post('/stelleninserate/abruf');
      notifyInfo('Abruf gestartet. Die Tabelle aktualisiert sich, bis er fertig ist.');
      await begleiteLauf();
    } catch (err) {
      notifyError(fehlermeldung(err));
      // A 409 means a run was already going: follow that one instead.
      if (err.response?.status === 409) await begleiteLauf();
    }
  });

  // A run already going when the page opens - started by the schedule, the
  // script or another tab - is followed the same way.
  if (laufendeZeilen().length) begleiteLauf();
}
